import {
  Questionnaire,
  QuestionnaireCategory,
  QuestionnaireCategoryItemsItem,
} from '@/api/generated-catch-backend/model';
import { getListQuestionnaireResponsesV2QuestionnairesQuestionnaireIdResponsesGetQueryKey } from '@/api/generated-catch-backend/questionnaire/questionnaire';
import { getQuestionnaireRoute } from '@/app/components/quizletv2/quizlet-config';
import {
  useGetOrCreateQuestionnaireResponse,
  useGetQuestionnaireResponseWithAnswers,
  useListQuestionnaires,
  useListResponseQuestions,
} from '@/app/services/questionnaireV2.service';
import { queryClient } from '@/client/query';
import { getRoute } from '@/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Interstitial, Question } from '../components/types';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';

export function useQuestionnaireSession(providedQuestionnaire?: Questionnaire) {
  const { state, dispatch } = useQuestionnaireContext();
  const navigate = useNavigate();
  const initializedQuestionnaireId = useRef<string | null>(null);

  // Environment variable for risk assessment
  const RISK_ASSESSMENT_UUID = import.meta.env.VITE_RISK_ASSESSMENT_UUID;

  // Always fetch questionnaires from backend (unless explicitly provided)
  const shouldFetchQuestionnaires = !providedQuestionnaire;

  const {
    data: allQuestionnaires,
    isLoading: isLoadingQuestionnaires,
    error: questionnaireError,
  } = useListQuestionnaires(shouldFetchQuestionnaires);

  // Memoize the selected questionnaire to prevent unnecessary re-renders
  const selectedQuestionnaire = useMemo(() => {
    if (providedQuestionnaire) {
      return providedQuestionnaire;
    }

    if (isLoadingQuestionnaires) {
      return null;
    }

    if (allQuestionnaires?.data && allQuestionnaires.data.length > 0) {
      // Check if we're on the risk assessment route
      const isRiskAssessment = window.location.pathname.startsWith('/risk-assessment');

      if (isRiskAssessment && RISK_ASSESSMENT_UUID) {
        // Find the risk assessment questionnaire specifically
        const riskAssessmentQuestionnaire = allQuestionnaires.data.find((q) => q.id === RISK_ASSESSMENT_UUID);
        if (riskAssessmentQuestionnaire) {
          return riskAssessmentQuestionnaire;
        }
      }

      // Default to first questionnaire if no specific match
      return allQuestionnaires.data[0];
    }

    return null;
  }, [providedQuestionnaire, allQuestionnaires, isLoadingQuestionnaires, RISK_ASSESSMENT_UUID]);

  // Process questionnaire when it changes - memoized to avoid unnecessary processing
  const processedData = useMemo(() => {
    if (!selectedQuestionnaire) {
      return null;
    }

    try {
      const allQuestions: (Question | Interstitial)[] = [];
      const questionToCategoryMap = new Map<number, number>();
      selectedQuestionnaire.categories.forEach((category: QuestionnaireCategory, categoryIndex: number) => {
        category.items.forEach((item: QuestionnaireCategoryItemsItem) => {
          if (item.questionnaireItemKind === 'question' || item.questionnaireItemKind === 'interstitial') {
            questionToCategoryMap.set(allQuestions.length, categoryIndex);
            allQuestions.push(item);
          }
        });
      });

      return {
        questions: allQuestions,
        questionToCategory: questionToCategoryMap,
        questionnaire: selectedQuestionnaire,
      };
    } catch (error) {
      return null;
    }
  }, [selectedQuestionnaire]);

  // Update context state when processed data changes
  useEffect(() => {
    if (
      processedData &&
      processedData.questionnaire &&
      initializedQuestionnaireId.current !== processedData.questionnaire.id
    ) {
      dispatch({ type: 'SET_QUESTIONNAIRE', payload: processedData.questionnaire });
      dispatch({
        type: 'SET_QUESTIONS',
        payload: {
          questions: processedData.questions,
          questionToCategory: processedData.questionToCategory,
        },
      });
      dispatch({ type: 'SET_LOADING', payload: false });
      initializedQuestionnaireId.current = processedData.questionnaire.id;
    }
  }, [processedData, dispatch]);

  // Only call useGetOrCreateQuestionnaireResponse when we have a valid questionnaire ID
  const hasValidQuestionnaireId = !!selectedQuestionnaire?.id;

  const {
    getOrCreateResponse,
    isLoading: isLoadingQuestionnaireResponse,
    existingResponse,
    hasExistingResponse,
  } = useGetOrCreateQuestionnaireResponse(selectedQuestionnaire?.id || '');

  // Override loading state when no questionnaire ID
  const actualIsLoadingQuestionnaireResponse = hasValidQuestionnaireId ? isLoadingQuestionnaireResponse : false;

  // Get existing answers if we have a response to restore
  const shouldFetchAnswers = !!(hasExistingResponse && existingResponse && selectedQuestionnaire);
  const { data: responseWithAnswers, isLoading: isLoadingAnswers } = useGetQuestionnaireResponseWithAnswers(
    selectedQuestionnaire?.id || '',
    existingResponse?.id || '',
    shouldFetchAnswers,
  );

  // Get enhanced questions list with server-driven session restoration context
  const shouldFetchEnhancedQuestions = !!(hasExistingResponse && existingResponse && selectedQuestionnaire);
  const { data: enhancedQuestionsData, isLoading: isLoadingEnhancedQuestions } = useListResponseQuestions(
    selectedQuestionnaire?.id || '',
    existingResponse?.id || '',
    shouldFetchEnhancedQuestions,
  );

  // Server-driven session restoration using enhanced questions data
  const getSessionRestorationData = useCallback(() => {
    if (enhancedQuestionsData) {
      return {
        lastAnsweredQuestion: enhancedQuestionsData.lastAnsweredQuestion,
        nextQuestion: enhancedQuestionsData.nextQuestion,
      };
    }
    return { lastAnsweredQuestion: null, nextQuestion: null };
  }, [enhancedQuestionsData]);

  // Session initialization and restoration logic - always ensure response exists on navigation
  useEffect(() => {
    const initializeQuestionnaireSession = async () => {
      if (
        !state.hasRestoredSession &&
        !state.isInitializingSession &&
        !actualIsLoadingQuestionnaireResponse &&
        !isLoadingAnswers &&
        !isLoadingEnhancedQuestions &&
        state.questions.length > 0
      ) {
        // Prevent concurrent initialization
        dispatch({ type: 'SET_INITIALIZING_SESSION', payload: true });

        try {
          // Always ensure we have a response (existing or newly created)
          const response = await getOrCreateResponse();

          // Handle null response (e.g., when 409 occurs but ID can't be extracted)
          if (!response) {
            // Still navigate to first question to allow user to continue
            if (state.questions.length > 0) {
              const firstQuestion = state.questions[0];
              dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: 0 });
                            dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
              const route = getRoute(getQuestionnaireRoute(selectedQuestionnaire), {
                questionSlug: firstQuestion.slug,
              });
              navigate(route);
            }
            dispatch({ type: 'SET_SESSION_RESTORED', payload: true });
            return;
          }

          // Set session state in context
          dispatch({
            type: 'SET_SESSION_STATE',
            payload: {
              responseId: response.questionnaireResponseId,
              },
          });

          // Set the existing response in context if available
          if (existingResponse) {
            dispatch({ type: 'SET_RESPONSE', payload: existingResponse });
          }

          // Determine where to navigate using server-driven session restoration
          const { lastAnsweredQuestion, nextQuestion } = getSessionRestorationData();

          if (hasExistingResponse && lastAnsweredQuestion) {
            // Navigate to the last answered question for review/editing
            const questionIndex = state.questions.findIndex((q) => q.slug === lastAnsweredQuestion);
            if (questionIndex !== -1) {
              dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: questionIndex });
                            dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
              navigate(getRoute(getQuestionnaireRoute(selectedQuestionnaire), { questionSlug: lastAnsweredQuestion }));
            }
          } else if (hasExistingResponse && nextQuestion) {
            // Navigate to the next unanswered question
            const questionIndex = state.questions.findIndex((q) => q.slug === nextQuestion.slug);
            if (questionIndex !== -1) {
              dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: questionIndex });
                            dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
              navigate(getRoute(getQuestionnaireRoute(selectedQuestionnaire), { questionSlug: nextQuestion.slug }));
            }
          } else {
            // New questionnaire session - navigate to first question
            if (state.questions.length > 0) {
              const firstQuestion = state.questions[0];
              dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: 0 });
                            dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
              const route = getRoute(getQuestionnaireRoute(selectedQuestionnaire), {
                questionSlug: firstQuestion.slug,
              });
              navigate(route);
            }
          }

          dispatch({ type: 'SET_SESSION_RESTORED', payload: true });
        } catch (error) {
          console.error('Failed to initialize questionnaire session:', error);
        } finally {
          dispatch({ type: 'SET_INITIALIZING_SESSION', payload: false });
        }
      }
    };

    initializeQuestionnaireSession();
  }, [
    hasExistingResponse,
    state.hasRestoredSession,
    state.isInitializingSession,
    actualIsLoadingQuestionnaireResponse,
    isLoadingAnswers,
    isLoadingEnhancedQuestions,
    existingResponse,
    getOrCreateResponse,
    dispatch,
    state.questions,
    responseWithAnswers,
    enhancedQuestionsData,
    shouldFetchAnswers,
    navigate,
    getSessionRestorationData,
    selectedQuestionnaire,
  ]);

  // Manual session restoration function (if needed)
  const restoreSession = async () => {
    try {
      // Prevent concurrent initialization
      if (state.isInitializingSession) {
        return null;
      }

      dispatch({ type: 'SET_INITIALIZING_SESSION', payload: true });

      // Ensure we have a response (existing or newly created)
      const response = await getOrCreateResponse();

      // Set session state in context
      if (response) {
        dispatch({
          type: 'SET_SESSION_STATE',
          payload: {
            responseId: response.questionnaireResponseId,
          },
        });
      }

      // Set the existing response in context if available
      if (existingResponse) {
        dispatch({ type: 'SET_RESPONSE', payload: existingResponse });
      }
      dispatch({ type: 'SET_SESSION_RESTORED', payload: true });
      dispatch({ type: 'SET_INITIALIZING_SESSION', payload: false });
      return response;
    } catch (error) {
      console.error('Failed to restore session:', error);
      dispatch({ type: 'SET_INITIALIZING_SESSION', payload: false });
      return null;
    }
  };

  // Reset session when questionnaire response is deleted
  const resetSession = async () => {
    try {
      // Reset session state flags
      dispatch({ type: 'SET_SESSION_RESTORED', payload: false });
      dispatch({ type: 'SET_INITIALIZING_SESSION', payload: true });

      // Clear current session data
      dispatch({
        type: 'SET_SESSION_STATE',
        payload: {
          responseId: null,
        },
      });
      dispatch({ type: 'SET_RESPONSE', payload: null });

      // Force refetch of questionnaire responses by invalidating cache
      // This will make useGetOrCreateQuestionnaireResponse detect no existing response
      if (selectedQuestionnaire?.id) {
        const queryKey = getListQuestionnaireResponsesV2QuestionnairesQuestionnaireIdResponsesGetQueryKey(
          selectedQuestionnaire.id,
        );
        await queryClient.invalidateQueries({ queryKey });
      }

      // Create a new response
      const response = await getOrCreateResponse();

      // Set new session state
      if (response) {
        dispatch({
          type: 'SET_SESSION_STATE',
          payload: {
            responseId: response.questionnaireResponseId,
          },
        });
      }

      // Navigate to first question (new response should always start from beginning)
      if (state.questions.length > 0) {
        const firstQuestion = state.questions[0];
        dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: 0 });
                dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
        navigate(getRoute(getQuestionnaireRoute(selectedQuestionnaire), { questionSlug: firstQuestion.slug }));
      }

      dispatch({ type: 'SET_SESSION_RESTORED', payload: true });
      dispatch({ type: 'SET_INITIALIZING_SESSION', payload: false });

      return response;
    } catch (error) {
      console.error('Failed to reset session:', error);
      dispatch({ type: 'SET_INITIALIZING_SESSION', payload: false });
      throw error;
    }
  };

  return {
    selectedQuestionnaire,
    questionnaire: state.questionnaire,
    questions: state.questions,
    questionToCategory: state.questionToCategory,
    responseWithAnswers: responseWithAnswers,
    isLoadingQuestionnaireResponse: actualIsLoadingQuestionnaireResponse || isLoadingAnswers,
    restoreSession,
    resetSession,
    getOrCreateResponse,
    hasRestoredSession: state.hasRestoredSession,
    hasExistingResponse,
    existingResponse,
    isRestoringSession: hasExistingResponse && !state.hasRestoredSession,
  };
}
